/**
 * EFX Search - Shopify Predictive Search System
 * Modern, modüler ve optimize edilmiş versiyon
 */

(function(window, document) {
  'use strict';

  // =====================================================
  // CONFIG
  // =====================================================
  const CONFIG = Object.freeze({
    MIN_QUERY_LENGTH: 2,
    RESULTS_LIMIT: 20,
    SEARCH_LIMIT: 12,
    SLIDE_SIZE: 4,
    DEBOUNCE_MS: 220,
    HOVER_DELAY_MS: 120,
    FETCH_TIMEOUT_MS: 8000,
    CACHE_TTL_MS: 45000,
    RECENT_SEARCHES_KEY: 'efx_recent_searches',
    RECENT_SEARCHES_MAX: 10,
    VENDOR_DISPLAY_LIMIT: 8,
    BREAKPOINT_DESKTOP: 1200
  });

  // =====================================================
  // STATE MANAGER
  // =====================================================
  class StateManager {
    constructor() {
      this._state = {
        isOpen: false,
        inSearchMode: false,
        searchResults: null,
        activeCatHandle: null,
        activeVendorName: null,
        searchReqId: 0
      };
      this._listeners = new Set();
    }

    get(key) {
      return this._state[key];
    }

    set(key, value) {
      const oldValue = this._state[key];
      if (oldValue !== value) {
        this._state[key] = value;
        this._notify(key, value, oldValue);
      }
    }

    update(updates) {
      Object.entries(updates).forEach(([key, value]) => this.set(key, value));
    }

    reset() {
      this.update({
        inSearchMode: false,
        searchResults: null,
        activeCatHandle: null,
        activeVendorName: null
      });
    }

    subscribe(listener) {
      this._listeners.add(listener);
      return () => this._listeners.delete(listener);
    }

    _notify(key, value, oldValue) {
      this._listeners.forEach(fn => {
        try { fn(key, value, oldValue); } catch (e) { console.error(e); }
      });
    }
  }

  // =====================================================
  // CACHE MANAGER
  // =====================================================
  class CacheManager {
    constructor(ttl = CONFIG.CACHE_TTL_MS) {
      this._cache = new Map();
      this._ttl = ttl;
    }

    get(key) {
      const entry = this._cache.get(key);
      if (!entry) return null;
      if (Date.now() - entry.ts > this._ttl) {
        this._cache.delete(key);
        return null;
      }
      return entry.data;
    }

    set(key, data) {
      this._cache.set(key, { ts: Date.now(), data });
    }

    clear() {
      this._cache.clear();
    }
  }

  // =====================================================
  // REQUEST MANAGER (Abort + Timeout)
  // =====================================================
  class RequestManager {
    constructor() {
      this._controllers = new Map();
    }

    cancel(key) {
      const ctrl = this._controllers.get(key);
      if (ctrl) {
        try { ctrl.abort(); } catch {}
        this._controllers.delete(key);
      }
    }

    cancelAll() {
      this._controllers.forEach((ctrl, key) => this.cancel(key));
    }

    async fetch(key, url, options = {}, timeout = CONFIG.FETCH_TIMEOUT_MS) {
      this.cancel(key);

      const controller = new AbortController();
      this._controllers.set(key, controller);

      const timeoutId = setTimeout(() => controller.abort('timeout'), timeout);

      try {
        const response = await fetch(url, {
          ...options,
          signal: controller.signal
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        return await response.json();
      } finally {
        clearTimeout(timeoutId);
        this._controllers.delete(key);
      }
    }
  }

  // =====================================================
  // DOM UTILITIES
  // =====================================================
  const DOM = {
    // Güvenli element oluşturma (XSS koruması)
    createElement(tag, attrs = {}, children = []) {
      const el = document.createElement(tag);
      
      Object.entries(attrs).forEach(([key, value]) => {
        if (key === 'className') {
          el.className = value;
        } else if (key === 'dataset') {
          Object.entries(value).forEach(([k, v]) => el.dataset[k] = v);
        } else if (key === 'style' && typeof value === 'object') {
          Object.assign(el.style, value);
        } else if (key.startsWith('on') && typeof value === 'function') {
          el.addEventListener(key.slice(2).toLowerCase(), value);
        } else {
          el.setAttribute(key, value);
        }
      });

      children.forEach(child => {
        if (typeof child === 'string') {
          el.appendChild(document.createTextNode(child));
        } else if (child instanceof Node) {
          el.appendChild(child);
        }
      });

      return el;
    },

    // Template literal'dan element (basit durumlar için)
    html(strings, ...values) {
      const template = document.createElement('template');
      template.innerHTML = strings.reduce((acc, str, i) => 
        acc + str + (DOM.escapeHtml(values[i]) ?? ''), ''
      );
      return template.content.cloneNode(true);
    },

    escapeHtml(str) {
      if (str == null) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    },

    // Query helper
    $(selector, context = document) {
      return context.querySelector(selector);
    },

    $$(selector, context = document) {
      return Array.from(context.querySelectorAll(selector));
    }
  };

  // =====================================================
  // UTILITIES
  // =====================================================
  const Utils = {
    debounce(fn, wait = CONFIG.DEBOUNCE_MS) {
      let timeoutId = null;
      const debounced = (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), wait);
      };
      debounced.cancel = () => clearTimeout(timeoutId);
      return debounced;
    },

    chunk(arr, size) {
      const chunks = [];
      for (let i = 0; i < arr.length; i += size) {
        chunks.push(arr.slice(i, i + size));
      }
      return chunks;
    },

    truncate(str, maxLength = 45) {
      if (!str || str.length <= maxLength) return str || '';
      return str.slice(0, maxLength).trim() + '…';
    },

    formatPrice(value) {
      if (value == null) return '';
      const num = parseFloat(value);
      if (isNaN(num)) return '';
      return num.toLocaleString('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }) + ' TL';
    },

    extractHandleFromUrl(url) {
      if (!url) return '';
      try {
        const pathname = new URL(url, location.origin).pathname;
        const parts = pathname.split('/').filter(Boolean);
        const idx = parts.indexOf('products');
        return idx !== -1 && parts[idx + 1] ? parts[idx + 1].toLowerCase() : '';
      } catch {
        return '';
      }
    },

    isCoarsePointer() {
      return window.matchMedia?.('(pointer: coarse)').matches || 'ontouchstart' in window;
    },

    isDesktop() {
      return window.innerWidth >= CONFIG.BREAKPOINT_DESKTOP;
    },

    async nextPaint() {
      await new Promise(r => requestAnimationFrame(r));
      await new Promise(r => requestAnimationFrame(r));
    }
  };

  // =====================================================
  // VENDOR HELPERS
  // =====================================================
  const VendorUtils = {
    getVendorUrl(vendorName) {
      if (!vendorName) return null;
      return `https://flavus.com/collections/vendors?q=${encodeURIComponent(vendorName.trim())}`;
    },

    buildSearchHref(query, vendorName = null) {
      let href = `/search?q=${encodeURIComponent(query)}&filter.v.availability=1&sort_by=relevance`;
      if (vendorName) {
        href += `&filter.p.vendor=${encodeURIComponent(vendorName)}`;
      }
      return href;
    }
  };

  // =====================================================
  // RECENT SEARCHES
  // =====================================================
  const RecentSearches = {
    _key: CONFIG.RECENT_SEARCHES_KEY,
    _max: CONFIG.RECENT_SEARCHES_MAX,

    get() {
      try {
        const raw = localStorage.getItem(this._key);
        const arr = raw ? JSON.parse(raw) : [];
        return Array.isArray(arr) ? arr : [];
      } catch {
        return [];
      }
    },

    add(query) {
      const term = String(query || '').trim();
      if (!term) return;

      let searches = this.get();
      searches = [term, ...searches.filter(q => 
        q.toLowerCase() !== term.toLowerCase()
      )].slice(0, this._max);

      localStorage.setItem(this._key, JSON.stringify(searches));
    },

    remove(query) {
      const term = String(query || '').trim();
      if (!term) return;

      let searches = this.get();
      searches = searches.filter(q => q.trim() !== term);
      localStorage.setItem(this._key, JSON.stringify(searches));
    },

    clear() {
      localStorage.removeItem(this._key);
    }
  };

  // =====================================================
  // API SERVICE
  // =====================================================
  class ApiService {
    constructor(requestManager, cacheManager) {
      this._request = requestManager;
      this._cache = cacheManager;
    }

    async fetchPredictive(query) {
      const cacheKey = `pred:${query}`;
      const cached = this._cache.get(cacheKey);
      if (cached) return cached;

      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product',
        'resources[limit]': CONFIG.SEARCH_LIMIT
      });

      const data = await this._request.fetch(
        'predictive',
        `/search/suggest.json?${params}`,
        { headers: { Accept: 'application/json' } },
        2200
      );

      this._cache.set(cacheKey, data);
      return data;
    }

    async fetchSearch(query) {
      const cacheKey = `search:${query}`;
      const cached = this._cache.get(cacheKey);
      if (cached) return cached;

      const url = `/search?view=efx-json&q=${encodeURIComponent(query)}`;
      const data = await this._request.fetch(
        'search',
        url,
        { headers: { Accept: 'application/json' } },
        3500
      );

      this._cache.set(cacheKey, data);
      return data;
    }

    async fetchPopularProducts(handle, limit = 24) {
      const cacheKey = `popular:${handle}:${limit}`;
      const cached = this._cache.get(cacheKey);
      if (cached) return cached;

      const url = `/collections/${handle}?view=efx-popular-json&limit=${limit}`;
      const data = await this._request.fetch(`popular:${handle}`, url, {}, 3500);
      
      // Response array veya { products: [] } olabilir
      const products = Array.isArray(data) ? data : (data?.products || data || []);
      
      this._cache.set(cacheKey, products);
      return products;
    }

  }

  // =====================================================
  // PRODUCT DEDUPLICATION
  // =====================================================
  const Deduplicator = {
    getKey(product) {
      if (product.id) return `id:${product.id}`;
      
      const handle = (product.handle || Utils.extractHandleFromUrl(product.url) || '')
        .trim().toLowerCase();
      if (handle) return `h:${handle}`;

      const title = (product.title || '').toLowerCase().trim();
      const price = parseFloat(product.price);
      return `tp:${title}:${isNaN(price) ? 'nan' : price.toFixed(2)}`;
    },

    dedupe(items) {
      const seen = new Set();
      return items.filter(item => {
        const key = this.getKey(item);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },

    normalize(product) {
      return {
        url: product.url,
        image: product.image,
        title: product.title,
        price: product.price,
        compareAtPrice: product.compareAtPrice,
        handle: product.handle || Utils.extractHandleFromUrl(product.url),
        vendor: product.vendor
      };
    }
  };

  // =====================================================
  // COMPONENTS
  // =====================================================

  // Spinner Component
  const Spinner = {
    render() {
      return DOM.createElement('div', { className: 'efx-loading' }, [
        DOM.createElement('div', {
          className: 'efx-spinner',
          'role': 'status',
          'aria-label': 'Yükleniyor'
        })
      ]);
    },

    show(container) {
      if (!container) return;
      container.innerHTML = '';
      container.appendChild(this.render());
    }
  };

  // No Results Component
  const NoResults = {
    render(query) {
      const container = DOM.createElement('div', {
        className: 'efx-no-results',
        style: {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '240px',
          textAlign: 'center',
          padding: '24px',
          gap: '16px'
        }
      });

      const icon = DOM.createElement('div', {
        style: {
          fontSize: '48px',
          lineHeight: '1',
          opacity: '0.4'
        }
      }, ['🔍']);

      const message = DOM.createElement('div', {
        style: {
          fontSize: '15px',
          color: '#555',
          lineHeight: '1.5'
        }
      });

      const strong = DOM.createElement('strong', {}, [query]);
      message.appendChild(document.createTextNode('Aramanıza uygun ürün bulunamadı: '));
      message.appendChild(strong);

      const hint = DOM.createElement('div', {
        style: {
          fontSize: '13px',
          color: '#888'
        }
      }, ['Farklı bir arama terimi deneyebilir veya anasayfaya dönebilirsiniz.']);

      const clearBtn = DOM.createElement('button', {
        type: 'button',
        className: 'efx-see-all',
        style: {
          marginTop: '8px',
          cursor: 'pointer',
          border: 'none',
          background: 'none'
        }
      }, ['Temizle']);

      clearBtn.addEventListener('click', () => {
        // Input'u temizle
        DOM.$$('.efx-main-search__input').forEach(input => {
          input.value = '';
        });
        // Default view'a dön
        if (window.efxSearch) {
          window.efxSearch.requests.cancelAll();
          window.efxSearch._showDefaultView();
          window.efxSearch._renderDefaultContent();
        }
      });

      container.appendChild(icon);
      container.appendChild(message);
      container.appendChild(hint);
      container.appendChild(clearBtn);

      return container;
    }
  };

  // Slider Component - eski kodun çalışan mantığıyla
  class Slider {
    constructor(container, items = []) {
      this.container = container;
      this.items = (items || []).slice(0, CONFIG.RESULTS_LIMIT);
      this.pages = Utils.chunk(this.items, CONFIG.SLIDE_SIZE);
      this.currentPage = 0;
    }

    render() {
      if (!this.container) return;
      
      const pages = this.pages;
      
      // innerHTML ile oluştur (eski kodun yöntemi)
      this.container.innerHTML = `
        <div class="efx-slider" data-page="0" aria-roledescription="carousel">
          <button class="efx-slide-btn efx-prev" aria-label="Önceki" ${pages.length <= 1 ? 'disabled' : ''}><i class="fa fa-chevron-left"></i></button>
          <div class="efx-track-wrap">
            <div class="efx-track" style="transform:translateX(0%)">
              ${pages.map(page => `
                <div class="efx-page">
                  ${page.map(product => this._cardHTML(Deduplicator.normalize(product))).join('')}
                </div>
              `).join('')}
            </div>
          </div>
          <button class="efx-slide-btn efx-next" aria-label="Sonraki" ${pages.length <= 1 ? 'disabled' : ''}><i class="fa fa-chevron-right"></i></button>
        </div>
      `;

      this._bindEvents();
    }

    _cardHTML(p) {
      const title = Utils.truncate(p.title, 38);
      const price = Utils.formatPrice(p.price);
      const hasDiscount = p.compareAtPrice && p.compareAtPrice > p.price;
      const compareAt = hasDiscount ? Utils.formatPrice(p.compareAtPrice) : null;
      
      let discountBadge = '';
      if (hasDiscount) {
        const discount = Math.round(((p.compareAtPrice - p.price) / p.compareAtPrice) * 100);
        discountBadge = `<div class="efx-discount-badge">-${discount}%</div>`;
      }

      return `
        <a class="efx-card" href="${p.url || '#'}">
          <div class="efx-card-media">
            ${p.image ? `<img loading="lazy" src="${p.image}" alt="">` : ''}
            ${discountBadge}
          </div>
          <div class="efx-card-details">
            <div class="efx-card-vendor">${DOM.escapeHtml(p.vendor || '')}</div>
            <div class="efx-card-title">${DOM.escapeHtml(title)}</div>
            ${price ? `<div class="efx-card-price">
              ${compareAt 
                ? `<span class="efx-card-compare">${compareAt}</span> <span class="efx-card-final">${price}</span>`
                : `<span class="efx-card-final">${price}</span>`}
            </div>` : ''}
          </div>
        </a>
      `;
    }

    _bindEvents() {
      const slider = this.container.querySelector('.efx-slider');
      const track = this.container.querySelector('.efx-track');
      const prevBtn = this.container.querySelector('.efx-prev');
      const nextBtn = this.container.querySelector('.efx-next');
      const pageCount = this.pages.length;

      if (!slider || !track || pageCount <= 1) return;

      const navigate = (direction) => {
        let page = parseInt(slider.dataset.page) || 0;
        if (direction === 'next') page = Math.min(page + 1, pageCount - 1);
        if (direction === 'prev') page = Math.max(page - 1, 0);
        
        slider.dataset.page = page;
        track.style.transform = `translateX(-${page * 100}%)`;
        if (prevBtn) prevBtn.disabled = page === 0;
        if (nextBtn) nextBtn.disabled = page === pageCount - 1;
      };

      prevBtn?.addEventListener('click', () => navigate('prev'));
      nextBtn?.addEventListener('click', () => navigate('next'));

      // Touch/swipe
      let startX = 0, deltaX = 0;
      track.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      track.addEventListener('touchmove', (e) => { deltaX = e.touches[0].clientX - startX; }, { passive: true });
      track.addEventListener('touchend', () => {
        if (Math.abs(deltaX) > 50) navigate(deltaX < 0 ? 'next' : 'prev');
        deltaX = 0;
      });
    }
  }

  // =====================================================
  // MAIN APPLICATION
  // =====================================================
  class EfxSearch {
    constructor(settings = {}) {
      this.settings = {
        popularCats: [],
        popularQueries: [],
        popularVendors: [],
        ...settings
      };

      // Core services
      this.state = new StateManager();
      this.cache = new CacheManager();
      this.requests = new RequestManager();
      this.api = new ApiService(this.requests, this.cache);

      // DOM references
      this.elements = {};

      // Bound methods
      this._handleInputFocus = this._handleInputFocus.bind(this);
      this._handleInputChange = this._handleInputChange.bind(this);
      this._handleOverlayClick = this._handleOverlayClick.bind(this);
      this._handleKeydown = this._handleKeydown.bind(this);
      this._handleResize = Utils.debounce(this._updateLayout.bind(this), 100);

      // Debounced search
      this._debouncedSearch = Utils.debounce(this._performSearch.bind(this), CONFIG.DEBOUNCE_MS);

      this._init();
    }

    // ---- Initialization ----
    _init() {
      this._injectStyles();
      this._createOverlay();
      this._bindGlobalEvents();
    }

    _injectStyles() {
      if (document.getElementById('efx-spinner-styles')) return;

      const css = `
        .efx-loading { display: flex; align-items: center; justify-content: center; min-height: 240px; }
        .efx-spinner { width: 42px; height: 42px; border: 3px solid rgba(0,0,0,.15); border-top-color: currentColor; border-radius: 50%; animation: efx-spin .8s linear infinite; }
        @keyframes efx-spin { to { transform: rotate(360deg); } }
        
      `;

      const style = document.createElement('style');
      style.id = 'efx-spinner-styles';
      style.textContent = css;
      document.head.appendChild(style);
    }

    _createOverlay() {
      // Check if already exists
      let overlay = DOM.$('.efx-search-overlay');
      
      if (!overlay) {
        overlay = DOM.createElement('div', { className: 'efx-search-overlay' });
        
        const panel = DOM.createElement('div', { className: 'custom-search-results' });
        const wrapper = DOM.createElement('div', { className: 'custom-search-results__wrapper' });

        // Default view
        const defaultView = this._createDefaultView();
        
        // Results view
        const resultsView = DOM.createElement('div', { 
          className: 'efx-search-results',
          style: { display: 'none' }
        });

        wrapper.appendChild(defaultView);
        wrapper.appendChild(resultsView);
        panel.appendChild(wrapper);
        overlay.appendChild(panel);
        document.body.appendChild(overlay);
      }

      // Store references
      this.elements = {
        overlay,
        panel: DOM.$('.custom-search-results', overlay),
        wrapper: DOM.$('.custom-search-results__wrapper', overlay),
        defaultView: DOM.$('.efx-search-default', overlay),
        resultsView: DOM.$('.efx-search-results', overlay)
      };
    }

    _createDefaultView() {
      const view = DOM.createElement('div', { className: 'efx-search-default' });

      view.innerHTML = `
        <div class="efx-upper-bar">
          <div class="efx-vendors-bar">
            <h4 style="margin-bottom:0;">Popüler Markalar</h4>
            <ul class="efx-pop-vendors"></ul>
          </div>
          <div style="display:flex;">
            <div class="efx-popUp-close">✖</div>
          </div>
        </div>
        <div class="efx-cols">
          <aside class="efx-sidebar">
            <h4>Popüler Kategoriler</h4>
            <ul class="efx-pop-cats"></ul>
            <h4>Popüler Aramalar</h4>
            <ul class="efx-pop-queries"></ul>
          </aside>
          <section class="efx-main">
            <div class="efx-section-title">Popüler Ürünler</div>
            <div class="efx-product-grid"></div>
            <div class="efx-search-all-products"></div>
          </section>
        </div>
        <div class="efx-bottom">
          <h4 style="margin-bottom:0;">Geçmiş Aramalar</h4>
          <ul class="efx-pop-recent-search"></ul>
        </div>
      `;

      // Bind close button
      const closeBtn = DOM.$('.efx-popUp-close', view);
      closeBtn?.addEventListener('click', () => this.close());

      return view;
    }

    _createResultsView() {
      const view = this.elements.resultsView;
      if (!view) return null;

      view.innerHTML = `
        <div class="efx-upper-bar">
          <div class="efx-vendors-bar">
            <h4 style="margin-bottom:0;">Popüler Markalar</h4>
            <ul class="efx-pop-vendors"></ul>
          </div>
          <div style="display:flex;">
            <div class="efx-popUp-close">✖</div>
          </div>
        </div>
        <div class="efx-cols">
          <aside class="efx-sidebar">
            <h4>Arama</h4>
            <div class="efx-query"></div>
          </aside>
          <section class="efx-main">
            <div class="efx-section-title">Ürünler</div>
            <div class="efx-product-grid"></div>
            <div class="efx-search-all-products"></div>
          </section>
        </div>
      `;

      // Bind close button
      const closeBtn = DOM.$('.efx-popUp-close', view);
      closeBtn?.addEventListener('click', () => this.close());

      return DOM.$('.efx-product-grid', view);
    }

    _bindGlobalEvents() {
      // Input listeners
      DOM.$$('.efx-main-search__input').forEach(input => {
        input.addEventListener('focus', this._handleInputFocus);
        input.addEventListener('click', this._handleInputFocus);
        input.addEventListener('input', this._handleInputChange);
      });

      // Overlay click
      this.elements.overlay?.addEventListener('click', this._handleOverlayClick);

      // Keyboard
      document.addEventListener('keydown', this._handleKeydown);

      // Resize
      window.addEventListener('resize', this._handleResize);
      window.addEventListener('scroll', this._handleResize);
    }

    // ---- Event Handlers ----
    _handleInputFocus() {
      this.open();
    }

    async _handleInputChange(e) {
      const query = e.target.value.trim();

      if (query.length < CONFIG.MIN_QUERY_LENGTH) {
        this._showDefaultView();
        this.requests.cancelAll();
        return;
      }

      // Show spinner immediately
      await this._showSearchViewWithSpinner(query);
      
      // Debounced search
      this._debouncedSearch(query);
    }

    _handleOverlayClick(e) {
      if (e.target === this.elements.overlay) {
        this.close();
      }
    }

   _handleKeydown(e) {
      if (e.key === 'Escape') {
        this.close();
      }
      
      // Enter tuşuna basıldığında aramayı kaydet ve sayfaya git
      if (e.key === 'Enter') {
        const input = DOM.$('.efx-main-search__input');
        const query = input?.value?.trim();
        if (query && query.length >= CONFIG.MIN_QUERY_LENGTH) {
          RecentSearches.add(query);
          window.location.href = VendorUtils.buildSearchHref(query);
        }
      }
  }

    // ---- Public Methods ----
    open() {
      if (this.state.get('isOpen')) return;

      this.state.update({
        isOpen: true,
        inSearchMode: false,
        searchResults: null
      });

      this.elements.overlay?.classList.add('is-open');
      document.body.classList.add('efx-no-scroll');

      this._showDefaultView();
      this._renderDefaultContent();
      this._updateLayout();

      // Check if input has content
      const input = DOM.$('.efx-main-search__input');
      const query = input?.value?.trim() || '';
      
      if (query.length >= CONFIG.MIN_QUERY_LENGTH) {
        this._handleInputChange({ target: input });
      }
    }

    close() {
      this.state.set('isOpen', false);
      this.elements.overlay?.classList.remove('is-open');
      document.body.classList.remove('efx-no-scroll');
      
      this.requests.cancelAll();
      this.state.reset();
      this._updateLayout();
    }

    // ---- View Management ----
    _showDefaultView() {
      this.state.update({
        inSearchMode: false,
        searchResults: null
      });

      if (this.elements.defaultView) {
        this.elements.defaultView.style.display = '';
      }
      if (this.elements.resultsView) {
        this.elements.resultsView.style.display = 'none';
      }

      // Load popular products if needed
      const cats = this.settings.popularCats;
      if (cats.length && !this.state.get('activeCatHandle')) {
        this.state.set('activeCatHandle', cats[0].handle);
        this._loadPopularProducts(cats[0].handle);
      }
    }

    async _showSearchViewWithSpinner(query) {
      if (this.elements.defaultView) {
        this.elements.defaultView.style.display = 'none';
      }
      if (this.elements.resultsView) {
        this.elements.resultsView.style.display = '';
      }

      const grid = this._createResultsView();
      const queryEl = DOM.$('.efx-query', this.elements.resultsView);
      
      if (queryEl) {
        queryEl.textContent = `"${query}"`;
      }

      Spinner.show(grid);
      await Utils.nextPaint();
    }

    // ---- Data Loading ----
    async _performSearch(query) {
      const reqId = this.state.get('searchReqId') + 1;
      this.state.set('searchReqId', reqId);

      try {
        // Parallel fetch
        const [predResult, searchResult] = await Promise.allSettled([
          this.api.fetchPredictive(query),
          this.api.fetchSearch(query)
        ]);

        // Check if still current request
        if (reqId !== this.state.get('searchReqId')) return;

        // Extract products
        const predProducts = predResult.status === 'fulfilled'
          ? (predResult.value?.resources?.results?.products || [])
          : [];
        
        const searchProducts = searchResult.status === 'fulfilled' && Array.isArray(searchResult.value)
          ? searchResult.value
          : [];

        // Merge and dedupe
        const merged = Deduplicator.dedupe([...predProducts, ...searchProducts]);

        this.state.update({
          inSearchMode: true,
          searchResults: merged
        });

        // Render — sonuç boşsa no-results göster
        this._renderSearchResults(merged, query);

      } catch (err) {
        if (err.name === 'AbortError') return;
        
        console.error('[EfxSearch] Search error:', err);
        this.state.update({
          inSearchMode: false,
          searchResults: null
        });

        if (this.elements.resultsView) {
          this.elements.resultsView.innerHTML = `
            <div class="efx-error">Arama hatası: ${DOM.escapeHtml(err.message)}</div>
          `;
        }
      }
    }

    async _loadPopularProducts(handle) {
      if (!handle) return;

      try {
        const grid = DOM.$('.efx-product-grid', this.elements.defaultView);
        Spinner.show(grid);
        await Utils.nextPaint();

        const products = await this.api.fetchPopularProducts(handle);
        
        this.state.set('activeCatHandle', handle);
        this.state.set('activeVendorName', null);

        this._renderProducts(products, this.elements.defaultView);
        this._updateCategoryActiveState(handle);
        this._renderSeeAllButton();

      } catch (err) {
        console.error(`[EfxSearch] Popular products error (${handle}):`, err);
      }
    }

    // ---- Rendering ----
    _renderDefaultContent() {
      this._renderPopularVendors(this.settings.popularVendors);
      this._renderPopularCategories(this.settings.popularCats);
      this._renderPopularQueries(this.settings.popularQueries);
      this._renderRecentSearches();
      this._renderSeeAllButton();
    }

    _renderPopularVendors(vendors) {
      const container = DOM.$('.efx-pop-vendors', this.elements.defaultView);
      if (!container) return;

      container.innerHTML = '';
      
      (vendors || []).forEach(vendor => {
        const li = DOM.createElement('li', { dataset: { vendorHandle: vendor.handle } });
        const link = DOM.createElement('a', { href: `/collections/${vendor.handle}` }, [vendor.title]);
        li.appendChild(link);
        container.appendChild(li);

        if (!Utils.isCoarsePointer()) {
          this._addHoverIntent(li, () => {
            this._loadPopularProducts(vendor.handle);
          });
        }
      });
    }

    _renderPopularCategories(categories) {
      const container = DOM.$('.efx-pop-cats', this.elements.defaultView);
      if (!container) return;

      container.innerHTML = '';
      
      // Skip first (default) category for display
      const visibleCats = categories.slice(1);
      const activeHandle = this.state.get('activeCatHandle');

      visibleCats.forEach(cat => {
        const isActive = cat.handle === activeHandle;
        const li = DOM.createElement('li', {
          dataset: { handle: cat.handle },
          className: isActive ? 'is-active' : ''
        });

        const link = DOM.createElement('a', { href: `/collections/${cat.handle}` });
        const catContainer = DOM.createElement('div', { className: 'cat-container' });
        catContainer.appendChild(DOM.createElement('span', { className: 'cat-title' }, [cat.title]));
        catContainer.appendChild(DOM.createElement('span', { className: 'cat-arrow' }, ['→']));
        link.appendChild(catContainer);
        li.appendChild(link);
        container.appendChild(li);

        if (!Utils.isCoarsePointer()) {
          this._addHoverIntent(li, () => {
            this.state.set('activeCatHandle', cat.handle);
            this._updateCategoryActiveState(cat.handle);
            this._loadPopularProducts(cat.handle);
          });
        }
      });
    }

    _renderPopularQueries(queries) {
      const container = DOM.$('.efx-pop-queries', this.elements.defaultView);
      if (!container) return;

      container.innerHTML = '';

      (queries || []).forEach(query => {
        const li = DOM.createElement('li', { dataset: { query } });
        const link = DOM.createElement('a', {
          href: `/search?q=${encodeURIComponent(query)}`,
          className: 'query-container'
        });
        link.appendChild(DOM.createElement('span', { className: 'query-title' }, [query]));
        link.appendChild(DOM.createElement('span', { className: 'cat-arrow' }, ['→']));
        li.appendChild(link);
        container.appendChild(li);

        if (!Utils.isCoarsePointer()) {
          this._addHoverIntent(li, () => {
            this._clearActiveStates();
            li.classList.add('is-active');
            this._previewQuery(query);
          });
        }
      });
    }

    _renderRecentSearches() {
      const container = DOM.$('.efx-pop-recent-search', this.elements.defaultView);
      if (!container) return;

      const searches = RecentSearches.get();
      
      if (!searches.length) {
        container.innerHTML = '';
        return;
      }

      container.innerHTML = '';

      searches.forEach(query => {
        const li = DOM.createElement('li', {
          className: 'efx-recent-li',
          style: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '6px',
            whiteSpace: 'nowrap'
          }
        });

        const itemBtn = DOM.createElement('button', {
          className: 'efx-recent-item',
          type: 'button',
          dataset: { q: query },
          style: { flex: '1', textAlign: 'left' }
        }, [query]);

        const removeBtn = DOM.createElement('button', {
          className: 'efx-recent-remove',
          type: 'button',
          dataset: { q: query },
          title: 'Kaldır',
          'aria-label': 'Kaldır'
        }, ['×']);

        itemBtn.addEventListener('click', () => {
          window.location.href = VendorUtils.buildSearchHref(query);
        });

        removeBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          RecentSearches.remove(query);
          li.remove();
          
          if (!RecentSearches.get().length) {
            container.innerHTML = '';
          }
        });

        li.appendChild(itemBtn);
        li.appendChild(removeBtn);
        container.appendChild(li);
      });
    }

    _renderSearchResults(products, query) {
      const grid = DOM.$('.efx-product-grid', this.elements.resultsView);
      const seeAllContainer = DOM.$('.efx-search-all-products', this.elements.resultsView);

      // ── Sonuç yoksa: "ürün bulunamadı" göster ──
      if (!products || products.length === 0) {
        if (grid) {
          grid.innerHTML = '';
          grid.appendChild(NoResults.render(query));
        }

        // "Tüm Ürünleri Görüntüle" yerine anasayfaya dön butonu koy
        if (seeAllContainer) {
          seeAllContainer.innerHTML = '';
        }

        // Vendor listesini de temizle
        const vendorContainer = DOM.$('.efx-pop-vendors', this.elements.resultsView);
        if (vendorContainer) vendorContainer.innerHTML = '';

        this._updateLayout();
        return;
      }

      // ── Sonuç varsa: normal slider göster ──
      if (grid) {
        const slider = new Slider(grid, products);
        slider.render();
      }

      // Render vendor list
      const vendors = [...new Set(products.map(p => p.vendor).filter(Boolean))];
      this._renderSearchVendors(vendors, query);

      // Render see all button
      this._renderSeeAllButton(query);
      this._updateLayout();
    }

    _renderSearchVendors(vendors, query) {
      const container = DOM.$('.efx-pop-vendors', this.elements.resultsView);
      if (!container) return;

      container.innerHTML = '';

      vendors.slice(0, CONFIG.VENDOR_DISPLAY_LIMIT).forEach(vendorName => {
        const href = VendorUtils.getVendorUrl(vendorName);

        const li = DOM.createElement('li', { dataset: { vendorName } });
        const link = DOM.createElement('a', { href }, [vendorName]);
        li.appendChild(link);
        container.appendChild(li);

        if (!Utils.isCoarsePointer()) {
          this._addHoverIntent(li, () => {
            if (!this.state.get('inSearchMode')) return;
            
            this.state.set('activeVendorName', vendorName);
            this._updateVendorActiveState(vendorName, container);

            const results = this.state.get('searchResults') || [];
            const filtered = results.filter(p => p.vendor === vendorName);
            
            const grid = DOM.$('.efx-product-grid', this.elements.resultsView);
            if (grid) {
              const slider = new Slider(grid, filtered);
              slider.render();
            }

            this._renderSeeAllButton(query);
          });
        }
      });
    }

    _renderProducts(products, container) {
      const grid = DOM.$('.efx-product-grid', container);
      if (!grid) return;

      const slider = new Slider(grid, products);
      slider.render();
    }

    _renderSeeAllButton(query = null) {
      const defaultContainer = DOM.$('.efx-search-all-products', this.elements.defaultView);
      const resultsContainer = DOM.$('.efx-search-all-products', this.elements.resultsView);

     if (this.state.get('inSearchMode') && query) {
    if (resultsContainer) {
      const vendorName = this.state.get('activeVendorName');
      const href = VendorUtils.buildSearchHref(query, vendorName);
      resultsContainer.innerHTML = '';
      
      const link = DOM.createElement('a', { className: 'efx-see-all', href }, ['Tüm Ürünleri Görüntüle']);
      
      // ✅ Tıklandığında aramayı kaydet
      link.addEventListener('click', () => {
        RecentSearches.add(query);
      });
      
      resultsContainer.appendChild(link);
    }
  } else {
        // Default mode
        if (defaultContainer) {
          const catHandle = this.state.get('activeCatHandle');
          const vendorName = this.state.get('activeVendorName');
          let href = '/';
          
          if (catHandle) href = `/collections/${catHandle}`;
          else if (vendorName) href = `/collections/${vendorName}`;

          defaultContainer.innerHTML = '';
          defaultContainer.appendChild(
            DOM.createElement('a', { className: 'efx-see-all', href }, ['Tüm Ürünleri Görüntüle'])
          );
        }
      }
    }

    // ---- Helper Methods ----
    async _previewQuery(query) {
      try {
        const data = await this.api.fetchPredictive(query);
        const products = (data?.resources?.results?.products || []).slice(0, CONFIG.RESULTS_LIMIT);
        this._renderProducts(products, this.elements.defaultView);
      } catch (err) {
        console.error(`[EfxSearch] Query preview error (${query}):`, err);
      }
    }

    _addHoverIntent(element, onIntent, delay = CONFIG.HOVER_DELAY_MS) {
      let timeoutId = null;

      element.addEventListener('mouseenter', () => {
        timeoutId = setTimeout(onIntent, delay);
      });

      element.addEventListener('mouseleave', () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      });
    }

    _clearActiveStates() {
      const selectors = ['.efx-pop-cats li', '.efx-pop-vendors li', '.efx-pop-queries li'];
      selectors.forEach(selector => {
        DOM.$$(selector, this.elements.defaultView).forEach(el => el.classList.remove('is-active'));
      });
    }

    _updateCategoryActiveState(handle) {
      DOM.$$('.efx-pop-cats li', this.elements.defaultView).forEach(li => {
        li.classList.toggle('is-active', li.dataset.handle === handle);
      });
      
      // Clear other active states
      DOM.$$('.efx-pop-vendors li.is-active', this.elements.defaultView).forEach(el => el.classList.remove('is-active'));
      DOM.$$('.efx-pop-queries li.is-active', this.elements.defaultView).forEach(el => el.classList.remove('is-active'));
    }

    _updateVendorActiveState(vendorName, container) {
      DOM.$$('li', container).forEach(li => {
        li.classList.toggle('is-active', li.dataset.vendorName === vendorName);
      });
    }

    _updateLayout() {
      const isNarrow = !Utils.isDesktop();
      const inSearchMode = this.state.get('inSearchMode');

      // Update sidebar visibility
      DOM.$$('.efx-sidebar', this.elements.overlay).forEach(sidebar => {
        const inResults = sidebar.closest('.efx-search-results');
        const shouldHide = isNarrow && inSearchMode && inResults;
        sidebar.style.display = shouldHide ? 'none' : '';
      });

      // Update overlay top position
      const scrollY = window.scrollY;
      const topPx = Utils.isDesktop()
        ? 112
        : 118;
      
      this.elements.overlay?.style.setProperty('--efx-overlay-top', `${topPx}px`);
    }

    // ---- Cleanup ----
    destroy() {
      this.requests.cancelAll();
      this._debouncedSearch.cancel();
      
      // Remove event listeners
      DOM.$$('.efx-main-search__input').forEach(input => {
        input.removeEventListener('focus', this._handleInputFocus);
        input.removeEventListener('click', this._handleInputFocus);
        input.removeEventListener('input', this._handleInputChange);
      });

      this.elements.overlay?.removeEventListener('click', this._handleOverlayClick);
      document.removeEventListener('keydown', this._handleKeydown);
      window.removeEventListener('resize', this._handleResize);
      window.removeEventListener('scroll', this._handleResize);

      // Remove DOM
      this.elements.overlay?.remove();
    }
  }

  // =====================================================
  // INITIALIZATION
  // =====================================================
  
  // Auto-initialize on DOM ready
  const init = () => {
    const settings = window.EFX_SETTINGS || {};
    window.efxSearch = new EfxSearch(settings);

    // Legacy API compatibility
    window.efxOpen = () => window.efxSearch.open();
    window.efxClose = () => window.efxSearch.close();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})(window, document);